/**
 * Flow component
 * ReactFlowを表示するコンポーネント
 */

import React, { useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Panel,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  NodeTypes,
  EdgeTypes,
  useReactFlow,
} from '@xyflow/react';
import TopicNode from '@/components/map/nodes/TopicNode';
import PhaseNode from '@/components/map/nodes/PhaseNode';
import ContentNode from '@/components/map/nodes/ContentNode';
import CustomEdge from '@/components/map/edges/CustomEdge';
import PhaseEdge from '@/components/map/edges/PhaseEdge';
import TimelineRuler from '@/components/map/TimelineRuler';
import ControlPanel from '@/components/map/ControlPanel';
import { useMapUI } from '@/contexts/map';

/**
 * ノードタイプの定義
 */
const nodeTypes: NodeTypes = {
  topicNode: TopicNode,
  phaseNode: PhaseNode,
  contentNode: ContentNode,
};

/**
 * エッジタイプの定義
 */
const edgeTypes: EdgeTypes = {
  customEdge: CustomEdge,
  phaseEdge: PhaseEdge,
};

/**
 * ノードデータの型
 */
interface NodeData {
  topicId?: string;
  [key: string]: any;
}

/**
 * Flowコンポーネントのプロパティ
 */
interface FlowProps {
  initialNodes: Node<NodeData>[];
  initialEdges: Edge[];
  onNodeClick: (nodeId: string) => void;
  onEdgeClick: (edgeId: string) => void;
  layoutType: string;
}

/**
 * Flowコンポーネント
 */
const Flow: React.FC<FlowProps> = ({ 
  initialNodes, 
  initialEdges, 
  onNodeClick, 
  onEdgeClick,
  layoutType 
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView } = useReactFlow();
  const { animateLayout } = useMapUI();
  
  // ノードとエッジが更新されたときにビューを更新
  useEffect(() => {
    if (initialNodes.length > 0) {
      const transitionOptions = animateLayout 
        ? { duration: 800, easing: 'easeInOutCubic' }
        : { duration: 0 };
      
      setNodes(ns => {
        // 既存ノードに対する位置更新
        const existingNodes = ns.map(existingNode => {
          const updatedNode = initialNodes.find(n => n.id === existingNode.id);
          if (updatedNode) {
            return {
              ...existingNode,
              position: updatedNode.position,
              data: {
                ...existingNode.data,
                ...updatedNode.data,
              },
              // 既存ノードにはトランジションをつける
              style: {
                ...existingNode.style,
                transition: animateLayout ? 'transform 800ms ease-in-out, opacity 500ms ease' : 'none'
              }
            };
          }
          return {
            ...existingNode,
            // 削除予定のノードをフェードアウト
            style: {
              ...existingNode.style,
              opacity: 0,
              transition: 'opacity 300ms ease'
            }
          };
        });

        // 削除されたノードを特定
        const currentIds = new Set(ns.map(n => n.id));
        const newIds = new Set(initialNodes.map(n => n.id));
        const nodesToRemove = ns.filter(n => !newIds.has(n.id));
        
        // 新しいノードを特定して追加
        const nodesToAdd = initialNodes.filter(n => !currentIds.has(n.id)).map(n => ({
          ...n,
          // 新しいノードはフェードイン
          style: {
            ...n.style,
            opacity: 0, // 初期状態は透明
            transition: animateLayout ? 'opacity 800ms ease-in-out' : 'none'
          }
        }));
        
        // 一定時間後に新しいノードをフェードイン
        if (nodesToAdd.length > 0 && animateLayout) {
          setTimeout(() => {
            setNodes(current => 
              current.map(n => {
                if (nodesToAdd.some(newNode => newNode.id === n.id)) {
                  return {
                    ...n,
                    style: {
                      ...n.style,
                      opacity: 1 // フェードイン
                    }
                  };
                }
                return n;
              })
            );
          }, 100);
        }
        
        // 削除予定のノードを一定時間後に実際に削除
        if (nodesToRemove.length > 0) {
          setTimeout(() => {
            setNodes(current => current.filter(n => newIds.has(n.id)));
          }, animateLayout ? 300 : 0);
          
          // 中間段階として現在のノードすべてを返す
          return existingNodes;
        }
        
        // 最終的なノードセットを返す
        return [...existingNodes.filter(n => newIds.has(n.id)), ...nodesToAdd];
      });
      
      // エッジの更新
      setEdges(es => {
        // 既存エッジの更新
        const existingEdges = es.map(existingEdge => {
          const updatedEdge = initialEdges.find(e => e.id === existingEdge.id);
          if (updatedEdge) {
            return {
              ...existingEdge,
              data: {
                ...existingEdge.data,
                ...updatedEdge.data,
              },
              style: {
                ...existingEdge.style,
                transition: animateLayout ? 'opacity 800ms ease-in-out' : 'none'
              }
            };
          }
          return {
            ...existingEdge,
            style: {
              ...existingEdge.style,
              opacity: 0,
              transition: 'opacity 300ms ease'
            }
          };
        });
        
        // 削除されたエッジを特定
        const currentEdgeIds = new Set(es.map(e => e.id));
        const newEdgeIds = new Set(initialEdges.map(e => e.id));
        const edgesToRemove = es.filter(e => !newEdgeIds.has(e.id));
        
        // 新しいエッジを特定して追加
        const edgesToAdd = initialEdges.filter(e => !currentEdgeIds.has(e.id)).map(e => ({
          ...e,
          style: {
            ...e.style,
            opacity: 0,
            transition: animateLayout ? 'opacity 800ms ease-in-out' : 'none'
          }
        }));
        
        // 一定時間後に新しいエッジをフェードイン
        if (edgesToAdd.length > 0 && animateLayout) {
          setTimeout(() => {
            setEdges(current => 
              current.map(e => {
                if (edgesToAdd.some(newEdge => newEdge.id === e.id)) {
                  return {
                    ...e,
                    style: {
                      ...e.style,
                      opacity: 1
                    }
                  };
                }
                return e;
              })
            );
          }, 300);
        }
        
        // 削除予定のエッジを一定時間後に実際に削除
        if (edgesToRemove.length > 0) {
          setTimeout(() => {
            setEdges(current => current.filter(e => newEdgeIds.has(e.id)));
          }, animateLayout ? 300 : 0);
          
          // 中間段階として現在のエッジすべてを返す
          return existingEdges;
        }
        
        // 最終的なエッジセットを返す
        return [...existingEdges.filter(e => newEdgeIds.has(e.id)), ...edgesToAdd];
      });
      
      // ノードが更新された後にビューをフィット
      setTimeout(() => {
        fitView({ 
          padding: 0.2, 
          duration: animateLayout ? 800 : 0,
          includeHiddenNodes: false
        });
      }, 100);
    }
  }, [initialNodes, initialEdges, setNodes, setEdges, fitView, animateLayout]);
  
  // レイアウトタイプが変更されたときにビューを再フィット
  useEffect(() => {
    setTimeout(() => {
      fitView({ 
        padding: 0.2, 
        duration: animateLayout ? 500 : 0,
        includeHiddenNodes: false
      });
    }, 100);
  }, [layoutType, fitView, animateLayout]);
  
  // ノードクリックハンドラー
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
    // フェーズノード以外の場合にのみトリガー
    if (node.type !== 'phaseNode') {
      // contentNodeの場合は、関連するトピックノードを開く
      if (node.type === 'contentNode' && node.data?.topicId) {
        onNodeClick(node.data.topicId);
      } else {
        onNodeClick(node.id);
      }
    }
  }, [onNodeClick]);
  
  // エッジクリックハンドラー
  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    onEdgeClick(edge.id);
    event.stopPropagation(); // イベントのバブリングを防止
  }, [onEdgeClick]);
  
  // タイムラインルーラーを表示するかどうか
  // コンテンツタイムラインレイアウトは使用しないためfalseに設定
  const showTimelineRuler = false; // layoutType === 'content-timeline';
  
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      onEdgeClick={handleEdgeClick}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      className="w-screen h-screen"
      nodesDraggable={false} // ノードのドラッグを無効化
      nodesConnectable={false} // ノードの接続を無効化
      elementsSelectable={true} // 要素の選択を有効化
      connectOnClick={false} // クリックによる接続を無効化
    >
      <Background gap={20} size={1} />
      <Controls position="bottom-right" />
      <Panel position="top-center" className="bg-transparent">
        <ControlPanel />
      </Panel>
      
      {/* コンテンツタイムラインの場合にのみ時間軸を表示 - 非表示に設定 */}
      {showTimelineRuler && (
        <Panel className="w-full bg-transparent">
          <TimelineRuler height={40} />
        </Panel>
      )}
    </ReactFlow>
  );
};

export default Flow;